
'use client';

import { useRouter } from 'next/navigation';
import React, { useCallback, useState, useEffect } from 'react';
import Image from 'next/image';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Thumbs, Navigation, Autoplay } from 'swiper/modules';
import { CiHeart } from 'react-icons/ci';
import { AK } from '@/constants';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

import type { Swiper as SwiperType } from 'swiper';
import { AxioseGET } from '@/utilities/axioscall';
import { checkerArray } from '@/utilities/checker';
import { AxiosError } from 'axios';
import banner from '../../../../public/image/bg.jpeg';
// import test1 from '@/public/image/test1.jpg';
import test1 from '@/public/assets/images/test1.jpg';
import OfferScroll from './OfferScroll';
import Link from 'next/link';
import { getLogo, getStoreName } from "@/services/storeService";
import { addtowishlist } from '@/clicks/wishlistClicks';


// Define interfaces for type safety
interface Product {
  id: string;
  name: string;
  seo_url: string;
  imageurl: string;
  mrp: number;
  sp: number;
}

interface Category {
  name: string;
  seo_url: string;
  products: Product[];
}

interface ImageSlide {
  imgURL: string;
  imgAlt: string;
}

type CategoryType = {
  name: string;
  seo_url: string;
  meta_tag_title: string;
  meta_tag_desc: string;
  cat_img: string | null;
  banner: string | null;
  subcat: number;
  parent_id: number;
  sortorder: number;
  id: number;
};

type ProductType = {
  id: number;
  name: string;
  sp: number;       // selling price
  mrp: number;      // maximum retail price
  imageurl: string; // product image url
  seo_url: string;  // product page slug
};

type DemoSliderProps = {
  categoryListData: CategoryType[];
  featuredcategoriesListData: CategoryType[];
  bestsellersListData: ProductType[];
  topratedListData: ProductType[];
};


const Homepage = ({
  categoryListData,
  featuredcategoriesListData,
  bestsellersListData,
  topratedListData,
}: DemoSliderProps) => {
  const [isMobile, setIsMobile] = useState(false);
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [pageinit, setPageInit] = useState(false);
  const [featuredcategoryproducts, setFeaturedCategorypageproducts] = useState<Category[]>([]);
  const [topratedCategorypageproducts, setTopratedCategorypageproducts] = useState<Product[]>([]);
  const [bestsellersCategorypageproducts, setBestsellersCategorypageproducts] = useState<Product[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [productwishlistData, setproductwishlistData] = useState<any[]>([]);
  const navigate = (moveon: string) => {
    router.push(moveon);
  };

  const router = useRouter();
  const assets = 'https://assetsvilva.blr1.cdn.digitaloceanspaces.com/zemdrop/';
  const productimage = '';
  const storeid = '';
  const store = '';

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const categoryhomepageproducts = useCallback(async () => {
    try {
      const res = await AxioseGET(AK.eFEATUREDPRODUCTSJSONAPI, { storeid }, false);
      if (res && res.data) {
        setFeaturedCategorypageproducts(res.data.featured_categories || []);
        setTopratedCategorypageproducts(res.data.top_rated || []);
        setBestsellersCategorypageproducts(res.data.best_sellers || []);
      }
    } catch (error) {
      const errors = error as AxiosError;
      console.error('Error fetching products:', errors);
    }
  }, [storeid]);

  useEffect(() => {
    if (!pageinit) {
      setPageInit(true);
      categoryhomepageproducts();
    }
  }, [pageinit, categoryhomepageproducts]);

  const navigateToDynamicPage = (seoUrl: string) => {
    router.push(`/p/${seoUrl}`);
  };

  const viewproductinfo = (product: Product) => {
    navigateToDynamicPage(product.seo_url);
  };

  const [hoveredProductId, setHoveredProductId] = useState<string | null>(null);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Implement search navigation if needed
    }
  };

  const images = [
    { imgURL: "https://assetsvilva.blr1.cdn.digitaloceanspaces.com/zemdrop/uploads/cqLZjtIQduUFzWPK0d8i7QCLtbWn90Pf7s6SfNlL.webp", imgAlt: 'Background Image 1' },
    { imgURL: "https://assetsvilva.blr1.cdn.digitaloceanspaces.com/zemdrop/uploads/yRD1KMUxmrrWzGmVN549H9AnnY0Ac99H7m0G4ae5.webp", imgAlt: 'Background Image 2' },
  ];

  return (
    <div className="">
      <OfferScroll />
      <div className="homepage-banner relative w-full overflow-hidden">
        <Swiper
          spaceBetween={30}
          centeredSlides={true}
          autoplay={{
            delay: 3200,
            disableOnInteraction: false,
            pauseOnMouseEnter: false,
          }}
          loop={true}
          modules={[Autoplay, Navigation]}
          navigation={{
            nextEl: '.custom-swiper-button-next',
            prevEl: '.custom-swiper-button-prev',
          }}
          className="mySwiper"
        >
          {images.map((image, index) => (
            <SwiperSlide key={index}>
              <Image
                width={1920} // Set to a high resolution for quality
                height={1080} // Matches typical banner aspect ratio
                src={image.imgURL}
                alt={image.imgAlt}
                className={`banner-slider${index + 1} w-full object-cover`} // Adjusted height, added rounded-lg for aesthetics
                loading="lazy"
              />
            </SwiperSlide>
          ))}

        </Swiper>
      </div>

      <div className="!max-w-7xl mx-auto !py-16">
        <div className="all-products-head container !pb-10">
          <p className="!text-xl !font-bold">{featuredcategoryproducts[1]?.name}</p>
        </div>

        <div className="!grid !grid-cols-2 !gap-4 sm:!grid-cols-3 md:!grid-cols-4 !px-[8px]">
          {!featuredcategoryproducts || featuredcategoryproducts.length === 0 ? (
            <div className="!loader-container !col-span-2 sm:!col-span-3 md:!col-span-4 !flex !justify-center">

              {/* <Link href="/" className="flex items-center gap-2 p-2"> */}
              <img
                className=""
                src={`${getLogo()}`}
                alt={`${getStoreName()} Logo`}
                width={160}
                height={50}
              />
              {/* </Link> */}
            </div>
          ) : (
            featuredcategoryproducts[1]?.products.map((product: Product) => {
              const defaultImage = checkerArray(product.imageurl?.split(','), 1)
                ? product.imageurl?.split(',')[0]
                : '';
              const hoverImage = checkerArray(product.imageurl?.split(','), 2)
                ? product.imageurl?.split(',')[1]
                : defaultImage;

              return (
                <div className="" key={product.id}>
                  <a
                    className="!block"
                    href="#"
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => e.preventDefault()}
                  >
                    <div className="!flex !flex-col">
                      <div
                        onClick={() => viewproductinfo(product)}
                        onMouseEnter={() => setHoveredProductId(product.id)}
                        onMouseLeave={() => setHoveredProductId(null)}
                      >
                        <div
                          style={{
                            width: '100%',
                            position: 'relative',
                            background: 'rgb(248, 246, 248)',
                          }}
                        >
                          <div className="!heart_icon !bg-[#1d1d1d4d] !w-fit !absolute !top-1 !right-1 !p-2 !rounded-full z-40">
                            {/* <CiHeart className="!text-white" size={20} /> */}
                            <svg
                              className="cursor-pointer"
                              onClick={async (e) => {
                                e.preventDefault(); // Prevent page scroll
                                e.stopPropagation(); // Prevent parent div click
                                await addtowishlist(
                                  product,
                                  productwishlistData,
                                  setproductwishlistData,
                                  navigate
                                );
                              }}
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="21"
                              viewBox="0 0 20 21"
                              fill={
                                productwishlistData?.includes(product?.id?.toString())
                                  ? "#f45440"
                                  : "none"
                              }
                            >
                              <path
                                d="M15.8333 11.9202C17.075 10.7035 18.3333 9.24521 18.3333 7.33687C18.3333 6.1213 17.8504 4.95551 16.9909 4.09597C16.1314 3.23643 14.9656 2.75354 13.75 2.75354C12.2833 2.75354 11.25 3.17021 9.99999 4.42021C8.74999 3.17021 7.71666 2.75354 6.24999 2.75354C5.03441 2.75354 3.86863 3.23643 3.00908 4.09597C2.14954 4.95551 1.66666 6.1213 1.66666 7.33687C1.66666 9.25354 2.91666 10.7119 4.16666 11.9202L9.99999 17.7535L15.8333 11.9202Z"
                                stroke={
                                  productwishlistData?.includes(product?.id?.toString())
                                    ? "#f45440"
                                    : "#fff"
                                }
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>


                          </div>
                          <img
                            src={
                              hoveredProductId === product.id
                                ? assets + productimage + hoverImage
                                : assets + productimage + defaultImage
                            }
                            onError={({ currentTarget }) => {
                              currentTarget.onerror = null;
                              currentTarget.src = '/yofte-assets/images/no-image.webp';
                            }}
                            className="!productImgTag !w-full !h-auto !object-cover"
                            title={product.name + store}
                            alt={product.name + store}
                            loading="lazy"
                            decoding="async"
                          />
                        </div>
                      </div>
                      <div className="!prod-des !p-1">
                        <h3 className="!bg !text-base !font-medium !text-[#304e58]">
                          {product.name.length > 22
                            ? `${product.name.slice(0, 25)}...`
                            : product.name}
                        </h3>
                        <div className="!flex !items-center !gap-x-2">
                          <h6 className='!text-[#304e58] !line-through'>Rs.{product.mrp}.00</h6>
                          <p className=" !text-[#304e58]">Rs.{product.sp}.00</p>
                        </div>
                      </div>
                    </div>
                  </a>
                </div>
              );
            })
          )}
        </div>

        {/* <a
          className="!flex !mx-auto !justify-center !bg-black !text-white !w-fit !py-3 !px-5 !my-[18px] !border-2 !border-black hover:!bg-[#000000b3]"
          href={`/products/${featuredcategoryproducts[1]?.seo_url}`}
        >
          View All
        </a> */}
      </div>

      <section className="!bg-[#ccd6d9] !py-16">
        <div className="!max-w-7xl mx-auto">
          <div className="!all-products-head container !pb-10">
            <p className="!text-xl !font-bold">{featuredcategoryproducts[2]?.name}</p>
          </div>

          <div className="!grid !grid-cols-2 !gap-4 sm:!grid-cols-3 md:!grid-cols-4 !px-[8px]">
            {!featuredcategoryproducts || featuredcategoryproducts.length === 0 ? (
              <div className="!loader-container !col-span-2 sm:!col-span-3 md:!col-span-4 !flex !justify-center">
                <img
                  className=""
                  src={`${getLogo()}`}
                  alt={`${getStoreName()} Logo`}
                  width={160}
                  height={50}
                />
              </div>
            ) : (
              featuredcategoryproducts[2]?.products.map((product: Product) => {
                const defaultImage = checkerArray(product.imageurl?.split(','), 1)
                  ? product.imageurl?.split(',')[0]
                  : '';
                const hoverImage = checkerArray(product.imageurl?.split(','), 2)
                  ? product.imageurl?.split(',')[1]
                  : defaultImage;

                return (
                  <div className="" key={product.id}>
                    <a
                      className="!block"
                      href="#"
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => e.preventDefault()}
                    >
                      <div className="!flex !flex-col">
                        <div
                          onClick={() => viewproductinfo(product)}
                          onMouseEnter={() => setHoveredProductId(product.id)}
                          onMouseLeave={() => setHoveredProductId(null)}
                        >
                          <div
                            style={{
                              width: '100%',
                              position: 'relative',
                              background: 'rgb(248, 246, 248)',
                            }}
                          >
                            <div className="!heart_icon !bg-[#1d1d1d4d] !w-fit !absolute !top-1 !right-1 !p-2 !rounded-full">
                              {/* <CiHeart className="!text-white" size={20} /> */}
                              <svg
                                className="cursor-pointer"
                                onClick={async (e) => {
                                  e.preventDefault(); // Prevent page scroll
                                  e.stopPropagation(); // Prevent parent div click
                                  await addtowishlist(
                                    product,
                                    productwishlistData,
                                    setproductwishlistData,
                                    navigate
                                  );
                                }}
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="21"
                                viewBox="0 0 20 21"
                                fill={
                                  productwishlistData?.includes(product?.id?.toString())
                                    ? "#f45440"
                                    : "none"
                                }
                              >
                                <path
                                  d="M15.8333 11.9202C17.075 10.7035 18.3333 9.24521 18.3333 7.33687C18.3333 6.1213 17.8504 4.95551 16.9909 4.09597C16.1314 3.23643 14.9656 2.75354 13.75 2.75354C12.2833 2.75354 11.25 3.17021 9.99999 4.42021C8.74999 3.17021 7.71666 2.75354 6.24999 2.75354C5.03441 2.75354 3.86863 3.23643 3.00908 4.09597C2.14954 4.95551 1.66666 6.1213 1.66666 7.33687C1.66666 9.25354 2.91666 10.7119 4.16666 11.9202L9.99999 17.7535L15.8333 11.9202Z"
                                  stroke={
                                    productwishlistData?.includes(product?.id?.toString())
                                      ? "#f45440"
                                      : "#fff"
                                  }
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>

                            </div>
                            <img
                              src={
                                hoveredProductId === product.id
                                  ? assets + productimage + hoverImage
                                  : assets + productimage + defaultImage
                              }
                              onError={({ currentTarget }) => {
                                currentTarget.onerror = null;
                                currentTarget.src = '/yofte-assets/images/no-image.webp';
                              }}
                              className="!productImgTag !w-full !h-auto !object-cover"
                              title={product.name + store}
                              alt={product.name + store}
                              loading="lazy"
                              decoding="async"
                            />
                          </div>
                        </div>
                        <div className="!prod-des !p-1">
                          <h3 className="!bg !text-base !font-medium !text-[#304e58]">
                            {product.name.length > 22
                              ? `${product.name.slice(0, 2)}...`
                              : product.name}
                          </h3>
                          <div className="!flex !items-center !gap-x-2">
                            <h6 className='!text-[#304e58] !line-through'>Rs. {product.mrp}.00</h6>
                            <p className=" !text-[#304e58]">Rs. {product.sp}.00</p>
                          </div>
                        </div>
                      </div>
                    </a>
                  </div>
                );
              })
            )}
          </div>

          {/* <a
            className="!flex !mx-auto !justify-center !bg-black !text-white !w-fit !py-3 !px-5 !my-[18px] !border-2 !border-black hover:!bg-[#000000b3]"
            href={`/products/${featuredcategoryproducts[2]?.seo_url}`}
          >
            View All
          </a> */}
        </div>
      </section>

      <div className="!max-w-7xl mx-auto !py-8 !px-4 md:!py-12">
        <div className="!flex !flex-wrap !bg-[#f0f0f0] !rounded-[8px]">
          <div className="!w-full md:!w-1/2">
            <Image
              height={1000}
              width={1000}
              src="https://assetsvilva.blr1.cdn.digitaloceanspaces.com/zemdrop/uploads/Tx2O9OBnEViuPOJIko3Y8NEjObKVYMSVPUfFug5T.webp"
              alt="Products"
              className="!w-full !h-auto !object-cover md:!rounded-tl-[8px] !rounded-tl-[8px] md:!rounded-bl-[8px] !rounded-bl-[0px] md:!rounded-tr-[0px] !rounded-tr-[8px]"
            />
          </div>
          <div className="!w-full md:!w-1/2 !flex !items-center md:!my-[0px] !my-[20px]">
            <div className="!px-4 md:!px-8 !text-center md:!text-start">
              <p className="!text-[14px] md:!text-[16px] !text-gray-700 !mb-6 ">
                Welcome to zemdrop – a brand created by a passionate duo with a mission to make high-quality,
                affordable products that celebrate individuality and brand value.
                “zemdrop” reflects our commitment to spreading colors, self improvement, and positivity.
                We believe life should always be vibrant and inspiring. Each product is thoughtfully crafted, blending timeless
                aesthetics with modern trends to resonate with our diverse community.
                We’re building a community of like-minded individuals who value positivity, quality, and inclusivity.
                Our collections are designed to celebrate individuality and unity,
                ensuring there’s something for everyone.
              </p>
              <h3 className="!text-[16px] md:!text-[20px] !font-bold !text-gray-900 !mb-6 !uppercase">
                Stay vibrant. Stay positive. Stay Zemdrop.
              </h3>
              <a href="/products/mensoversizedtshirt">
                <button className="!bg-[#000000] hover:!bg-[#000000bb] !text-white !font-semibold !py-3 !px-6 !transition !duration-300">
                  EXPLORE SHOP NOW!
                </button>
              </a>
            </div>
          </div>
        </div>
      </div>


      <div className="!client_review_sec !bg-[#f0f0f0]  !py-8 md:!py-12">
        <div className="max-w-6xl mx-auto">
          <Swiper
            spaceBetween={10}
            thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
            modules={[FreeMode, Thumbs, Autoplay]}
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
              pauseOnMouseEnter: false,
            }}
            onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
            className="!mySwiper2 !max-w-7xl mx-auto"
          >
            <SwiperSlide>
              <div className="!flex !flex-col !justify-center !text-center">
                <h6 className="!text-[34px] !font-bold">Our Happy Clients</h6>
                <p className="!font-semibold">
                  "When I needed a premium quality T-shirt, Zemdrop was the perfect choice. The fabric quality and comfort level were exceptional. Even my friends complimented it!"
                </p>
                <p className="!mt-3 !text-gray-500 !text-[12px] uppercase">- MR. Ravi Kumar</p>
                <p className="!text-black !text-[12px] uppercase">Tirupur</p>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className="!flex !flex-col !justify-center !text-center">
                <h6 className="!text-[34px] !font-bold">Our Happy Clients</h6>
                <p className="!font-semibold">
                  "The fit and design were just perfect for me. The price was reasonable, and I received a high-quality product. It was a great change compared to my usual brands!"
                </p>
                <p className="!mt-3 !text-gray-500 !text-[12px] uppercase">- MR. Jagadeesh</p>
                <p className="!text-black !text-[12px] uppercase">Chennai</p>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className="!flex !flex-col !justify-center !text-center">
                <h6 className="!text-[34px] !font-bold">Our Happy Clients</h6>
                <p className="!font-semibold">
                  "The T-shirt I purchased from Zemdrop was awesome. The color hasn’t faded, and the quality has stayed intact over time. I’ll definitely be purchasing again!"
                </p>
                <p className="!mt-3 !text-gray-500 !text-[12px] uppercase">- MR. Karthik</p>
                <p className="!text-black !text-[12px] uppercase">Tirichy</p>
              </div>
            </SwiperSlide>
          </Swiper>

          <Swiper
            onSwiper={setThumbsSwiper}
            slidesPerView={3}
            spaceBetween={10}
            freeMode={true}
            watchSlidesProgress={true}
            modules={[FreeMode, Thumbs, Navigation, Autoplay]}
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
              pauseOnMouseEnter: false,
            }}
            className="!p-[20px] !overflow-auto md:!w-[250px] !w-[220px] !flex !justify-between m !mx-auto"
          >
            <SwiperSlide>
              <Image
                src={test1}
                className="!rounded-full !mx-auto !cursor-pointer"
                alt="Customer 1"
                width={40}
                height={40}
                style={{
                  opacity: activeIndex === 0 ? 1 : 0.5,
                  transform: activeIndex === 0 ? 'scale(1.2)' : 'scale(1)',
                  transition: 'opacity 0.3s ease, transform 0.3s ease',
                }}
              />
            </SwiperSlide>
            <SwiperSlide>
              <Image
                src={test1}
                className="!rounded-full !mx-auto !cursor-pointer"
                alt="Customer 2"
                width={40}
                height={40}
                style={{
                  opacity: activeIndex === 1 ? 1 : 0.5,
                  transform: activeIndex === 1 ? 'scale(1.2)' : 'scale(1)',
                  transition: 'opacity 0.3s ease, transform 0.3s ease',
                }}
              />
            </SwiperSlide>
            <SwiperSlide>
              <Image
                src={test1}
                className="!rounded-full !mx-auto !cursor-pointer"
                alt="Customer 3"
                width={40}
                height={40}
                style={{
                  opacity: activeIndex === 2 ? 1 : 0.5,
                  transform: activeIndex === 2 ? 'scale(1.2)' : 'scale(1)',
                  transition: 'opacity 0.3s ease, transform 0.3s ease',
                }}
              />
            </SwiperSlide>
          </Swiper>
        </div>
      </div>

      <div className="!container mx-auto">
        <div className="!py-8 md:!py-8 !px-3 sm:!px-16 md:!mx-5 lg:!my-14 lg:!mx-44">
          <img
            src="https://assetsvilva.blr1.cdn.digitaloceanspaces.com/zemdrop/WebsiteImages/policy-logos.png"
            alt="Policy Logos"
            className="!img-fluid"
          />
        </div>
      </div>

      <div className="!zem-testimonial"></div>
    </div>
  );
};

export default Homepage;